import FactCheckIcon from "@mui/icons-material/FactCheck";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import {IconButton, Stack, Tooltip} from "@mui/material";
import Box from "@mui/material/Box";
import {ColumnDef} from "@tanstack/react-table";
import type {AggregationSurveyListItem} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {DataType, DataTypeOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import {SurveyStatus, SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import {SimpleChipElement} from "../../common/uiElements/SimpleChipElement";
import {SurveyFormatElement} from "../../common/uiElements/SurveyFormatElement";
import {SURVEY_STATUS_LABEL_KEYS, SurveyStatusElement} from "../../common/uiElements/SurveyStatusElement";
import {SURVEY_TYPE_LABEL_KEYS, SurveyTypeElement} from "../../common/uiElements/SurveyTypeElement";
import SettingsListPage from "../../system/layout/SettingsListPage";
import {isSurveyObjectId} from "../shared/surveyId.util";
import {surveyInvitationsLink, surveySubmissionsLink} from "../shared/surveyListLinks";
import {SURVEYS_TABLE_ID} from "../shared/surveyQueryRefresh";
import {filterEnumValues, filterTextValues} from "../shared/surveyTableFilters";
import SurveyCreateDialog from "./SurveyCreateDialog";

const SURVEY_STATUSES = Object.values(SurveyStatus);
const SURVEY_TYPES = Object.values(SurveyType);

const SurveyList = () => {
    useTrackScreenVisit('system surveys');

    const navigate = useNavigate();
    const {t} = useTranslation();
    const sectors = useAllSectors();

    const [createOpen, setCreateOpen] = useState(false);

    const columns = useMemo<ColumnDef<AggregationSurveyListItem>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: survey => survey.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 260,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: survey => survey.status,
            cell: ({row}) => <SurveyStatusElement status={row.original.status}/>,
            size: 140,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'questionCount',
            header: t('Questions'),
            accessorFn: survey => survey.questionCount,
            cell: ({row}) => <Box>{row.original.questionCount}</Box>,
            size: 140,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'submissionCount',
            header: t('Submissions'),
            accessorFn: survey => survey.submissionCount,
            cell: ({row}) => (
                <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                    <Box>{row.original.submissionCount}</Box>
                    <Box data-no-row-click>
                        <Tooltip title={t('View submissions')}>
                            <IconButton
                                size={'small'}
                                onClick={() => navigate(surveySubmissionsLink({surveyId: row.original.id}))}
                            >
                                <FactCheckIcon fontSize={'small'}/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Stack>
            ),
            size: 170,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'invitationCount',
            header: t('Invitations'),
            accessorFn: survey => survey.invitationCount,
            cell: ({row}) => (
                <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                    <Box>{row.original.invitationCount}</Box>
                    <Box data-no-row-click>
                        <Tooltip title={t('View invitations')}>
                            <IconButton
                                size={'small'}
                                onClick={() => navigate(surveyInvitationsLink({surveyId: row.original.id}))}
                            >
                                <ForwardToInboxIcon fontSize={'small'}/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Stack>
            ),
            size: 170,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'responseRate',
            header: t('Response rate'),
            accessorFn: survey => survey.invitationCount
                ? survey.submissionCount / survey.invitationCount
                : null,
            cell: ({row}) => (
                <Box>
                    {row.original.invitationCount
                        ? `${Math.round((row.original.submissionCount / row.original.invitationCount) * 100)}%`
                        : '—'}
                </Box>
            ),
            size: 150,
            meta: {defaultActive: true}
        },
        {
            id: 'type',
            header: t('Type'),
            accessorFn: survey => survey.type,
            cell: ({row}) => <SurveyTypeElement type={row.original.type}/>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'lastSubmissionAt',
            header: t('Last submission'),
            accessorFn: survey => survey.lastSubmissionAt,
            cell: ({row}) => <DateElement date={row.original.lastSubmissionAt}/>,
            size: 190,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'createdAt',
            header: t('Created'),
            accessorFn: survey => survey.createdAt,
            cell: ({row}) => <DateElement date={row.original.createdAt}/>,
            size: 190,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'format',
            header: t('Format'),
            accessorFn: survey => survey.format,
            cell: ({row}) => <SurveyFormatElement format={row.original.format}/>,
            size: 170,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'sectorNames',
            header: t('Sectors'),
            accessorFn: survey => survey.sectorNames,
            cell: ({row}) => <SimpleChipElement values={row.original.sectorNames}/>,
            size: 220,
            meta: {defaultActive: false}
        },
        {
            id: 'languages',
            header: t('Languages'),
            accessorFn: survey => survey.languages,
            cell: ({row}) => <LanguagesElement languageCodes={row.original.languages}/>,
            size: 150,
            meta: {defaultActive: false}
        },
        {
            id: 'lastUpdatedAt',
            header: t('Last updated'),
            accessorFn: survey => survey.lastUpdatedAt,
            cell: ({row}) => <DateElement date={row.original.lastUpdatedAt}/>,
            size: 190,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: survey => survey.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        }
    ], [t, navigate]);

    const filterOptions = useMemo<FilterOption[]>(() => {
        const sectorsWithId = sectors.filter(sector => !!sector.id);

        return [
            {
                field: 'status',
                name: t('Status'),
                localizations: {},
                description: '',
                allowedOperators: [DataTypeOperator.IN],
                predefinedValues: SURVEY_STATUSES.map(status => ({
                    cid: status,
                    name: t(SURVEY_STATUS_LABEL_KEYS[status] ?? status),
                    colorCode: ''
                })),
                enumValues: SURVEY_STATUSES,
                dataType: DataType.ENUM
            },
            {
                field: 'type',
                name: t('Type'),
                localizations: {},
                description: '',
                allowedOperators: [DataTypeOperator.IN],
                predefinedValues: SURVEY_TYPES.map(type => ({
                    cid: type,
                    name: t(SURVEY_TYPE_LABEL_KEYS[type] ?? type),
                    colorCode: ''
                })),
                enumValues: SURVEY_TYPES,
                dataType: DataType.ENUM
            },
            ...(sectorsWithId.length ? [{
                field: 'sectorId',
                name: t('Sector'),
                localizations: {},
                description: '',
                allowedOperators: [DataTypeOperator.IN],
                predefinedValues: sectorsWithId.map(sector => ({
                    cid: sector.id!,
                    name: sector.name || sector.id!,
                    colorCode: ''
                })),
                enumValues: sectorsWithId.map(sector => sector.id!),
                dataType: DataType.ENUM
            } satisfies FilterOption] : []),
            {
                field: 'id',
                name: t('ID'),
                localizations: {},
                description: '',
                allowedOperators: [DataTypeOperator.EQUAL],
                predefinedValues: [],
                dataType: DataType.TEXT
            }
        ];
    }, [sectors, t]);

    const controller = useServerTable<AggregationSurveyListItem>({
        tableId: SURVEYS_TABLE_ID,
        columns,
        getRowId: survey => survey.id,
        onRowClick: survey => navigate(`/system/surveys/${survey.id}`),
        initialSorting: [{id: 'createdAt', desc: true}],
        queryFn: ({pagination, searchQuery, filterRules, sorting}) => {
            const query = searchQuery.trim();
            // A pasted id is looked up as one - a name search could never match it.
            const searchesId = !!query && isSurveyObjectId(query);
            const filteredIds = filterTextValues(filterRules, 'id')?.map(id => id.trim()).filter(Boolean);
            // Search and filter narrow the same column, so with both set both have to hold.
            const ids = searchesId && filteredIds?.length
                ? filteredIds.filter(id => id === query)
                : searchesId ? [query] : filteredIds;

            return SurveyAggregationService.listSurveys({
                id: ids,
                name: query && !searchesId ? [query] : undefined,
                status: filterEnumValues(filterRules, 'status', SURVEY_STATUSES),
                type: filterEnumValues(filterRules, 'type', SURVEY_TYPES),
                sectorIds: filterTextValues(filterRules, 'sectorId'),
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
        }
    });

    return (
        <SettingsListPage title={t('Surveys')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch placeholder={t('Search surveys')}/>
                        <ServerTableSort/>
                        <ServerTableFilter options={filterOptions}/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create survey')}
                            onClick={() => setCreateOpen(true)}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            {createOpen && (
                <SurveyCreateDialog
                    open
                    onClose={() => setCreateOpen(false)}
                />
            )}
        </SettingsListPage>
    );
};

export default SurveyList;
